import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
} from '@mui/material';
import {
    FlexDiv,
    TextDefault,
    TextInput,
    TextLead,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import AutomationReminders from '../../components/manager/automation/reminders';
import {
    AUTOMATION_SCHEDULE_TYPE,
    REMINDER_DELAY_TYPE,
} from '../../utils/constants';
import { useStores } from '../../hooks/use-stores';
import RadioButton from '../../components/RadioButton';

export const DEFAULT_RECOMMENDATION_TRAINING_CONFIG = {
    nameTemplate: '{{assessmentName}} – {{groupName}}',
    listNameTemplate: '{{assessmentName}} – {{threatAreas}}',
    dueDays: 30,
    sendingTime: '08:00',
    scheduleType: AUTOMATION_SCHEDULE_TYPE.NOW,
    remindersEnabled: false,
    remindersChannel: 1,
    remindersSteps: [],
};

const TEMPLATE_HELP =
    'Use {{assessmentName}}, {{groupName}}, {{threatAreas}} / {{taNames}}, {{threatAreasCount}}, {{userCount}}, {{index}}, {{MMMM}}, {{YYYY}}, {{DD}}';

export function parseRecommendationTrainingConfig(uiSettings = {}) {
    let remindersSettings = { channel: 1, steps: [] };
    const raw =
        uiSettings['ui.assessment.recommendations.training.reminders_settings'];
    if (raw) {
        try {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (parsed && typeof parsed === 'object') {
                remindersSettings = {
                    channel:
                        typeof parsed.channel === 'number' ? parsed.channel : 1,
                    steps: Array.isArray(parsed.steps) ? parsed.steps : [],
                };
            }
        } catch {
            // keep default
        }
    }
    const dueDays = Number(
        uiSettings['ui.assessment.recommendations.training.due_days']
    );
    const scheduleType = Number(
        uiSettings['ui.assessment.recommendations.training.schedule_type']
    );
    return {
        nameTemplate:
            uiSettings[
                'ui.assessment.recommendations.training.name_template'
            ] || DEFAULT_RECOMMENDATION_TRAINING_CONFIG.nameTemplate,
        listNameTemplate:
            uiSettings[
                'ui.assessment.recommendations.training.list_name_template'
            ] || DEFAULT_RECOMMENDATION_TRAINING_CONFIG.listNameTemplate,
        dueDays:
            Number.isFinite(dueDays) && dueDays > 0
                ? dueDays
                : DEFAULT_RECOMMENDATION_TRAINING_CONFIG.dueDays,
        sendingTime:
            uiSettings[
                'ui.assessment.recommendations.training.sending_time'
            ] || DEFAULT_RECOMMENDATION_TRAINING_CONFIG.sendingTime,
        scheduleType: [0, 1, 2].includes(scheduleType)
            ? scheduleType
            : DEFAULT_RECOMMENDATION_TRAINING_CONFIG.scheduleType,
        remindersEnabled:
            uiSettings[
                'ui.assessment.recommendations.training.reminders_enabled'
            ] === '1',
        remindersChannel: remindersSettings.channel,
        remindersSteps: remindersSettings.steps.map((s) => ({ ...s })),
    };
}

export function toTrainingConfigPayload(draft) {
    return {
        nameTemplate: draft.nameTemplate,
        listNameTemplate: draft.listNameTemplate,
        dueDays: Number(draft.dueDays) || 30,
        sendingTime: draft.sendingTime,
        scheduleType: [0, 1, 2].includes(Number(draft.scheduleType))
            ? Number(draft.scheduleType)
            : AUTOMATION_SCHEDULE_TYPE.NOW,
        remindersEnabled: !!draft.remindersEnabled,
        remindersSettings: {
            channel:
                typeof draft.remindersChannel === 'number'
                    ? draft.remindersChannel
                    : 1,
            steps: Array.isArray(draft.remindersSteps)
                ? draft.remindersSteps
                : [],
        },
    };
}

export function scheduleTypeLabel(scheduleType) {
    if (scheduleType === AUTOMATION_SCHEDULE_TYPE.FUTURE) return 'Scheduled';
    if (scheduleType === AUTOMATION_SCHEDULE_TYPE.DOWNLOAD_LINKS)
        return 'Manual';
    return 'Now';
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object} [props.initial] draft fields
 * @param {(payload: object) => Promise<void>|void} props.onSave
 * @param {boolean} [props.saving]
 * @param {string} [props.title]
 * @param {string} [props.saveLabel]
 * @param {string} [props.subtitle]
 */
export default function RecommendationTrainingConfigModal({
    open,
    onClose,
    initial,
    onSave,
    saving = false,
    title = 'Configure recommendation training',
    saveLabel = 'Save',
    subtitle,
}) {
    const { store } = useStores();
    const [draft, setDraft] = useState(DEFAULT_RECOMMENDATION_TRAINING_CONFIG);

    useEffect(() => {
        if (open) {
            setDraft({
                ...DEFAULT_RECOMMENDATION_TRAINING_CONFIG,
                ...(initial || {}),
                remindersSteps: Array.isArray(initial?.remindersSteps)
                    ? initial.remindersSteps.map((s) => ({ ...s }))
                    : [],
            });
        }
    }, [open, initial]);

    const handleSave = async () => {
        await onSave(toTrainingConfigPayload(draft));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minWidth: 520,
                    maxWidth: 720,
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle>
                <TextLead bold>{title}</TextLead>
                {subtitle && (
                    <TextTiny lighter block style={{ marginTop: 4 }}>
                        {subtitle}
                    </TextTiny>
                )}
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <FlexDiv column gap={3}>
                    <FlexDiv column style={{ marginBottom: 16 }}>
                        <TextLead bold lighter>
                            Sending
                        </TextLead>
                        <FlexDiv gap={24} mt={8} mb={8}>
                            <RadioButton
                                id="recScheduleNow"
                                label="Now"
                                value={AUTOMATION_SCHEDULE_TYPE.NOW}
                                groupValue={draft.scheduleType}
                                onClick={(v) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        scheduleType: v,
                                    }))
                                }
                            />
                            <RadioButton
                                id="recScheduleFuture"
                                label="Scheduled"
                                value={AUTOMATION_SCHEDULE_TYPE.FUTURE}
                                groupValue={draft.scheduleType}
                                onClick={(v) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        scheduleType: v,
                                    }))
                                }
                            />
                            <RadioButton
                                id="recScheduleManual"
                                label="Manual"
                                value={AUTOMATION_SCHEDULE_TYPE.DOWNLOAD_LINKS}
                                groupValue={draft.scheduleType}
                                onClick={(v) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        scheduleType: v,
                                    }))
                                }
                            />
                        </FlexDiv>
                        <TextTiny lighter>
                            Now sends immediately. Scheduled starts at sending
                            time (next occurrence). Manual generates downloadable
                            sign-in links.
                        </TextTiny>
                    </FlexDiv>

                    <FlexDiv column style={{ marginBottom: 16 }}>
                        <TextLead bold lighter>
                            Training name template
                        </TextLead>
                        <TextInput
                            id="recommendation-training-name"
                            margin="none"
                            heightSmall
                            value={draft.nameTemplate}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    nameTemplate: e.target.value,
                                }))
                            }
                            placeholder="{{assessmentName}} – {{groupName}}"
                            size="small"
                            stretched
                        />
                        <TextDefault
                            lighter
                            style={{
                                fontSize: 12,
                                opacity: 0.8,
                                marginTop: -10,
                            }}
                        >
                            {TEMPLATE_HELP}
                        </TextDefault>
                    </FlexDiv>

                    <FlexDiv column style={{ marginBottom: 16 }}>
                        <TextLead bold lighter>
                            List name template
                        </TextLead>
                        <TextInput
                            id="recommendation-list-name"
                            margin="none"
                            heightSmall
                            value={draft.listNameTemplate}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    listNameTemplate: e.target.value,
                                }))
                            }
                            placeholder="{{assessmentName}} – {{threatAreas}}"
                            size="small"
                            stretched
                        />
                        <TextDefault
                            lighter
                            style={{
                                fontSize: 12,
                                opacity: 0.8,
                                marginTop: -10,
                            }}
                        >
                            {TEMPLATE_HELP}
                        </TextDefault>
                    </FlexDiv>

                    <FlexDiv column style={{ marginBottom: 16 }}>
                        <TextLead bold lighter>
                            Due in (days)
                        </TextLead>
                        <TextInput
                            margin="none"
                            heightSmall
                            select
                            stretched
                            value={String(draft.dueDays)}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    dueDays: Number(e.target.value) || 30,
                                }))
                            }
                        >
                            {[7, 14, 21, 30, 45, 60, 90].map((d) => (
                                <MenuItem key={d} value={String(d)}>
                                    {d} days
                                </MenuItem>
                            ))}
                        </TextInput>
                    </FlexDiv>

                    <FlexDiv column style={{ marginBottom: 16 }}>
                        <TextLead bold lighter>
                            Sending time
                        </TextLead>
                        <input
                            type="time"
                            value={draft.sendingTime}
                            onChange={(e) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    sendingTime: e.target.value,
                                }))
                            }
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: 4,
                                fontSize: 14,
                            }}
                        />
                        <TextDefault
                            lighter
                            style={{
                                fontSize: 12,
                                marginTop: 4,
                                opacity: 0.8,
                            }}
                        >
                            Used for scheduled start and reminder send times.
                        </TextDefault>
                    </FlexDiv>

                    <FlexDiv column>
                        <AutomationReminders
                            mt={0}
                            reminders_enabled={draft.remindersEnabled}
                            reminders_channel={draft.remindersChannel}
                            reminders_steps={draft.remindersSteps}
                            slackAvailable={store.slackAvailable ?? false}
                            teamsAvailable={store.teamsAvailable ?? false}
                            slackConnected={false}
                            teamsConnected={false}
                            onChangeRemindersEnabled={(e) => {
                                const enabled =
                                    typeof e === 'boolean'
                                        ? e
                                        : e?.target?.checked ??
                                          !draft.remindersEnabled;
                                setDraft((prev) => ({
                                    ...prev,
                                    remindersEnabled: enabled,
                                    remindersSteps: enabled
                                        ? prev.remindersSteps.length
                                            ? prev.remindersSteps
                                            : [
                                                  {
                                                      delay: 1,
                                                      delayType:
                                                          REMINDER_DELAY_TYPE.DAY,
                                                  },
                                              ]
                                        : [],
                                }));
                            }}
                            onChangeRemindersChannel={(channel) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    remindersChannel: channel,
                                }))
                            }
                            onAddReminderStep={() =>
                                setDraft((prev) => ({
                                    ...prev,
                                    remindersSteps: [
                                        ...prev.remindersSteps,
                                        {
                                            delay: 1,
                                            delayType: REMINDER_DELAY_TYPE.DAY,
                                        },
                                    ],
                                }))
                            }
                            onDeleteReminderStep={(step) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    remindersSteps: prev.remindersSteps.filter(
                                        (s) => s !== step
                                    ),
                                }))
                            }
                            onStepsChange={() =>
                                setDraft((prev) => ({ ...prev }))
                            }
                            scheduleType={
                                draft.scheduleType ===
                                AUTOMATION_SCHEDULE_TYPE.NOW
                                    ? AUTOMATION_SCHEDULE_TYPE.NOW
                                    : AUTOMATION_SCHEDULE_TYPE.FUTURE
                            }
                        />
                    </FlexDiv>
                </FlexDiv>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <ThemedButton
                    mode="secondary"
                    text="Cancel"
                    onClick={onClose}
                    disabled={saving}
                    $noTextTransform
                />
                <ThemedButton
                    mode="primary"
                    text={saveLabel}
                    onClick={handleSave}
                    disabled={saving}
                    $saving={saving}
                    $noTextTransform
                />
            </DialogActions>
        </Dialog>
    );
}
